import { useState, useEffect } from 'react';
import { api, apiPost, getToken } from '../api';
import { useI18n } from '../i18n/context';
import '../styles/ui.css';
import './ListPages.css';

export default function Backups() {
  const { t } = useI18n();
  const [backups, setBackups] = useState([]);
  const [backupsDir, setBackupsDir] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  function formatSize(bytes, sizeMb) {
    if (bytes === undefined || bytes === null) return `${sizeMb} MB`;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  useEffect(() => {
    loadBackups();
  }, []);

  async function loadBackups() {
    setError('');
    try {
      const data = await api('/health/backups');
      setBackups(data.backups || []);
      setBackupsDir(data.backupsDir || '');
    } catch (e) {
      setError(e.message || t('backups.errorLoad'));
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateBackup() {
    setError('');
    setCreating(true);
    try {
      await apiPost('/health/backups/run', {});
      await loadBackups();
    } catch (e) {
      setError(e.message || t('backups.errorCreate'));
    } finally {
      setCreating(false);
    }
  }

  async function handleDownload(filename) {
    try {
      const token = getToken();
      // Use getApiUrl-aware resolution indirectly via absolute fetch
      const rootUrl = window.location.hostname.includes('localhost')
        ? 'http://localhost:3000'
        : (window.location.hostname.includes('sbx') || window.location.hostname.includes('sandbox'))
          ? 'https://control-horas-backend-sbx.fly.dev'
          : 'https://control-horas-backend.fly.dev';
      const url = `${rootUrl}/health/backups/download/${filename}`;

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al descargar');
      }

      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    } catch (e) {
      setError(e.message || 'Error al descargar el archivo');
    }
  }

  if (loading) {
    return (
      <div className="sa-page-frame">
        <div className="sa-page-header">
          <h1 className="sa-page-title">{t('backups.title')}</h1>
          <p className="sa-page-subtitle">{t('backups.subtitle')}</p>
        </div>
        <div className="ui-page-content">
          <div className="sa-loading" style={{ padding: 16 }}>{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="sa-page-frame">
      <div className="sa-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="sa-page-title">{t('backups.title')}</h1>
          <p className="sa-page-subtitle">{t('backups.subtitle')}</p>
        </div>
        <button
          className="sa-btn-primary"
          onClick={handleCreateBackup}
          disabled={creating}
        >
          {creating ? t('backups.creating') : t('backups.createBtn')}
        </button>
      </div>

      <div className="ui-page-content">
        {error && (
          <div className="ui-alert ui-alert--danger" style={{ marginBottom: 16, padding: 12, borderRadius: 4, backgroundColor: '#fde8e8', color: '#9b1c1c' }}>
            {error}
          </div>
        )}

        {backupsDir && (
          <div style={{ marginBottom: 16, fontSize: '0.875rem', color: '#6b7280' }}>
            <strong>{t('backups.backupsDir')}:</strong> <code>{backupsDir}</code>
          </div>
        )}

        <div className="sa-table-wrapper" style={{ overflowX: 'auto', backgroundColor: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <table className="sa-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#374151' }}>{t('backups.name')}</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#374151' }}>{t('backups.size')}</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#374151' }}>{t('backups.date')}</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#374151', textAlign: 'right' }}>{t('backups.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {backups.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ padding: '24px', textAlign: 'center', color: '#9ca3af' }}>
                    {t('backups.noBackups')}
                  </td>
                </tr>
              ) : (
                backups.map((b) => (
                  <tr key={b.name} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 500, color: '#111827' }}>{b.name}</td>
                    <td style={{ padding: '12px 16px', color: '#4b5563' }}>{formatSize(b.sizeBytes, b.sizeMb)}</td>
                    <td style={{ padding: '12px 16px', color: '#4b5563' }}>{new Date(b.createdAt).toLocaleString()}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <button
                        className="sa-btn-secondary"
                        onClick={() => handleDownload(b.name)}
                        style={{ fontSize: '0.875rem', padding: '6px 12px' }}
                      >
                        {t('backups.download')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
